import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Link from '../../utils/link';

import Button from '../../atoms/button';
import CartButton from '../../molecules/cart-icon';
import HeartSVG from '../../atoms/icon-heart';
import LocaleTrigger from '../../molecules/locale-icon';
import LogoIconSVG from '../../public/images/logos/icon.svg';
import LogoTextSVG from '../../public/images/logos/text.svg';
import PetiteDropdownTrigger from '../petite-dropdown-trigger';
import SearchSVG from '../../atoms/icon-search';
import UserGreeting from '../../molecules/user-greeting';

import withNavigationActions from '../../organisms/navigation';
import { responsiveCSS } from '../../theme/page';
import { useEventListener } from '../../utils/hooks';
import trackEvent from '../../utils/tracking';

const Header = styled.header`
  background-color: ${(props) => props.theme.color.backgroundLight};
  display: flex;
  position: sticky;
  top: 42px;
  z-index: ${(props) => props.theme.layers.centerstage};
  overflow: hidden;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    height: 12rem;
    padding-bottom: 0;
    top: -17px;
  }
`;

const HeaderWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  padding-bottom: 1rem;
  background-color: ${(props) => props.theme.color.backgroundLight};
  border: none;
  flex: 1;
  padding-top: 1rem;
  position: relative;
  transform: ${(props) => (props.isActive ? 'translate3d(28rem, 0, 0)' : '')};
  transition: transform ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuart};

  @media (min-width: ${(props) => props.theme.breakpoint.small}) {
    /* 38rem = navigation width */
    transform: ${(props) => (props.isActive ? 'translate3d(38rem, 0, 0)' : '')};
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    transform: translate3d(0,0,0);

    > div {
      align-self: flex-end;
    }
  }

  ${responsiveCSS}

  padding-left: 0;
  padding-right: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
`;

const HeaderLogo = styled.a`
  align-items: center;
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  height: 4.4rem;
  justify-content: center;
`;

const LogoText = styled(LogoTextSVG)`
  max-width: 123px;
  width: 100%;
`;

const LogoIcon = styled(LogoIconSVG)`
  display: none;
  height: 35px;
  max-width: 28px;
  width: 100%;

  path {
    fill: ${(props) => props.theme.color.brand}
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: block;
    left: 50%;
    position: absolute;
    transform: translate(-50%, -130%);
  }
`;

const HeaderRight = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  white-space: nowrap;
`;

const MenuButton = styled(Button)`
  align-items: center;
  background-color: transparent;
  border-color: transparent;
  display: flex;
  height: 4.4rem;
  width: 4.4rem;
  outline: ${(props) => (props.isActive ? 'none' : 'initial')};
  overflow: initial;
  padding: 1rem;
  position: relative;
  z-index: ${(props) => (props.isActive ? '2' : 'initial')};

  :hover {
    opacity: 100;
  }

  i,
  i::before,
  i::after {
    display: block;
    background-color: ${(props) => props.theme.color.brand};
    height: 1px;
    transition: transform ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuart};
    width: 100%;
  }

  i {
    text-indent: -9999px;
    position: relative;
    background-color: ${(props) => (props.isActive ? 'transparent' : '')};
    z-index: ${({ theme }) => theme.layers.audience};

    ::before,
    ::after {
      content: '';
      position: absolute;
    }

    ::before {
      top: -7px;
      transform: ${(props) => (props.isActive ? 'translate3d(0, 7px, 0) rotate(-45deg)' : '')};
    }

    ::after {
      bottom: -7px;
      transform: ${(props) => (props.isActive ? 'translate3d(0, -7px, 0) rotate(45deg)' : '')};
    }
  }

  ::after {
    background-color: ${(props) => props.theme.color.background};
    content: '';
    height: ${(props) => (props.isActive ? '200vh' : '0')};
    /*  side navigation width - elem padding - elem border width */
    left: calc(-28rem - 1rem - 2px);
    opacity: ${(props) => (props.isActive ? '0.9' : '0')};
    overflow: hidden;
    position: absolute;
    top: -50vh;
    width: 150vw;
    transition: opacity ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuart};
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }
`;

const SearchButton = styled(Button)`
  background-color: transparent;
  border-color: transparent;
  height: 4.4rem;
  outline: none;
  overflow: initial;
  padding: 1rem;
  position: relative;

  i {
    display: block;
    text-indent: -9999px;
  }

  svg {
    display: block;
    height: 2rem;
    width: 2rem;
    fill: ${(props) => props.theme.color.brand};
  }
`;

const PetiteProfileMobileWrapper = styled.div`
  position: sticky;
  top: 102px;
  background-color: ${(props) => props.theme.color.backgroundLight};
  display: flex;
  align-items: flex-start;
  height: 2.8rem;
  max-height: 2.8rem;
  justify-content: center;
  user-select: none;
  transform: translate3d(0,0,0);
  transition: transform ${(props) => props.theme.animation.slow} ease;
  z-index: ${(props) => props.theme.layers.upstage - 1};

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }
`;

const PetiteProfileDesktopWrapper = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    background-color: ${(props) => props.theme.color.backgroundLight};
    height: 2.8rem;
    display: flex;
    justify-content: center;
    user-select: none;
    align-items: center;
    height: 4.4rem;
    padding: 1rem;
    position: relative;
  }
`;

const WishlistButton = styled(Button)`
  background-color: transparent;
  border-color: transparent;
  display: none;
  height: 4.4rem;
  outline: ${(props) => (props.isActive ? 'none' : 'initial')};
  overflow: initial;
  padding: 1rem;
  position: relative;
  align-items: center;

  svg {
    display: block;
    height: 2.7rem;
    width: 2.7em;
    fill: transparent;
    stroke: ${(props) => props.theme.color.brand};
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
  }
`;

const LocalButton = styled(LocaleTrigger)`
  display: none;
  padding-right: 1rem;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
  }
`;

const Overlay = styled.div`
  background-color: ${(props) => props.theme.color.background};
  height: ${(props) => (props.isActive ? '100vh' : '0')};
  /*  side navigation width - elem padding - elem border width */
  left: 0;
  opacity: ${(props) => (props.isActive ? '0.9' : '0')};
  overflow: hidden;
  pointer-events: none;
  position: fixed;
  top: 0;
  width: 100vw;
  transition: opacity ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuart};

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    height: 0;
    opacity: 0;
  }
`;

export const NavigationHeader = (props) => {
  const handleOnNavigationClick = () => {
    if (!props.isNavigationActive) {
      trackEvent({
        eventCategory: 'Navigation',
        eventAction: 'Burger Menu Opened',
        eventNonInteraction: true
      });
    }
    props.toggleNavigation(!props.isNavigationActive);
  };

  const handleOnSearchClick = () => {
    trackEvent({
      event: 'techEvent',
      eventCategory: 'Search',
      eventAction: 'Open Search Box'
    });
    props.toggleGlobalSearch(!props.isGlobalSearchActive);
  };

  const handleOnCartClick = () => props.toggleCartModal(!props.isCartActive);

  const isLoggedIn = useCallback(() => !!props.profileEmail, [props.profileEmail]);

  const petiteWrapperRef = useRef();

  let ticking = false;
  let previousY = 0;

  const requestTick = () => {
    const { window } = global;
    const { current } = petiteWrapperRef;

    if (current && !ticking) {
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const isScrollingDown = currentY > previousY;

        if (current.style && currentY !== previousY) {
          if (isScrollingDown && currentY > 100) {
            current.style.transform = 'translate3d(0, -97px, 0)';
          } else {
            current.style.transform = 'translate3d(0, 0, 0)';
          }
        }

        previousY = currentY;
        ticking = false;
      });
    }

    ticking = true;
  };

  useEventListener('scroll', requestTick);

  return (
    <>
      <Header>
        <Overlay isActive={props.isNavigationActive} />

        <HeaderWrapper isActive={props.isNavigationActive}>
          <HeaderLeft>
            <MenuButton
              isIcon
              isActive={props.isNavigationActive}
              onClick={handleOnNavigationClick}
              title="Toggle Navigation"
            >
              <i>Toggle Navigation</i>
            </MenuButton>

            <LocalButton country={props.locale} currency={props.currency} />

            <SearchButton
              aria-label="open search"
              data-test-id="open-search"
              isIcon
              isActive={props.isNavigationActive}
              onClick={handleOnSearchClick}
              title="Search"
            >
              <SearchSVG />
              <i>Search</i>
            </SearchButton>
          </HeaderLeft>

          <Link href="/" passHref>
            <HeaderLogo title="Maisonette - Home Page">
              <LogoIcon />
              <LogoText />
            </HeaderLogo>
          </Link>

          <HeaderRight>
            <Link href={props.profileId ? '/account' : '/login'} passHref>
              <UserGreeting
                aria-label="account details button"
                isLoggedIn={isLoggedIn()}
                name={props.profileFirstName || undefined}
                title="Account"
                isLink
              />
            </Link>

            {
              isLoggedIn() && (
                <PetiteProfileDesktopWrapper title="Petite Profiles">
                  <PetiteDropdownTrigger />
                </PetiteProfileDesktopWrapper>
              )
            }

            <Link href="/lists/wishlist" passHref>
              <WishlistButton
                aria-label="open wishlist"
                data-test-id="open-wishlist"
                isActive={props.isNavigationActive}
                role="link"
                title="Wish List"
                isLink
              >
                <HeartSVG />
              </WishlistButton>
            </Link>

            <CartButton
              aria-label="open cart"
              data-test-id="open-cart"
              count={props.cartCount}
              onClick={handleOnCartClick}
              title="Bag"
            />
          </HeaderRight>
        </HeaderWrapper>
      </Header>

      {
        isLoggedIn() && (
          <PetiteProfileMobileWrapper title="Petite Profiles" ref={petiteWrapperRef}>
            <PetiteDropdownTrigger />
          </PetiteProfileMobileWrapper>
        )
      }
    </>
  );
};

NavigationHeader.defaultProps = {
  cartCount: 0,
  currency: 'USD',
  isCartActive: false,
  isGlobalSearchActive: false,
  isNavigationActive: false,
  locale: 'EN',
  profileId: null,
  profileEmail: null,
  profileFirstName: null,
  toggleCartModal: () => { },
  toggleGlobalSearch: () => { },
  toggleNavigation: () => { }
};

NavigationHeader.propTypes = {
  cartCount: PropTypes.number,
  currency: PropTypes.string,
  isCartActive: PropTypes.bool,
  isGlobalSearchActive: PropTypes.bool,
  isNavigationActive: PropTypes.bool,
  locale: PropTypes.string,
  toggleCartModal: PropTypes.func,
  toggleGlobalSearch: PropTypes.func,
  toggleNavigation: PropTypes.func,
  profileId: PropTypes.number,
  profileEmail: PropTypes.string,
  profileFirstName: PropTypes.string
};

const ConnectedNavigationHeader = withNavigationActions(NavigationHeader);

ConnectedNavigationHeader.displayName = 'NavigationHeader';

NavigationHeader.whyDidYouRender = true;

export default ConnectedNavigationHeader;
